import { ApolloLink } from '@apollo/client';
import { getMainDefinition } from '@apollo/client/utilities';

import { clarityEvent, clarityTag } from 'src/lib/clarity';

// ---------------------------------------------------------------------------
// Apollo Link — Clarity event telemetry for ALL GraphQL mutations
// ---------------------------------------------------------------------------

/**
 * Forward-link that emits Clarity API events for every GraphQL mutation.
 *
 * For each mutation, fires three events using the operation name:
 *   {operationName}_intent   — mutation request sent
 *   {operationName}_success  — server returned data without errors
 *   {operationName}_error    — server returned GraphQL errors or network failure
 *
 * Session tags:
 *   mutation_name      — last operation name (filterability in Clarity)
 *   mutation_page      — route prefix that fired it (e.g. "sales-orders")
 *   mutation_outcome   — "success" | "error"
 *   mutation_duration  — bucketed latency for the most recent mutation
 *
 * All Clarity calls are wrapped in try/catch so telemetry never blocks real
 * application behavior — both inbound (intent) and outbound (response).
 */

/** Bucket a latency in ms into a Clarity-friendly label (low cardinality). */
function durationBucket(ms: number): string {
  if (ms < 100) return '<100ms';
  if (ms < 300) return '100-300ms';
  if (ms < 1000) return '300ms-1s';
  if (ms < 3000) return '1-3s';
  return '>3s';
}

export const clarityEventsLink = new ApolloLink((operation, forward) => {
  const definition = getMainDefinition(operation.query);

  // Only track mutations — skip queries and subscriptions
  if (definition.kind !== 'OperationDefinition' || definition.operation !== 'mutation') {
    return forward(operation);
  }

  const name = operation.operationName;
  if (!name) {
    return forward(operation);
  }

  const startedAt = performance.now();

  try {
    // Strip numeric path segments (entity IDs) so the tag stays low-cardinality.
    const pageSection = window.location.pathname
      .split('/')
      .filter((s) => s && !/^\d+$/.test(s))
      .join('/');
    clarityTag('mutation_name', name);
    clarityTag('mutation_page', pageSection || '/');
    clarityEvent(`${name}_intent`);
  } catch {
    // Never block a GraphQL request for telemetry
  }

  return forward(operation).map((response) => {
    try {
      const hasErrors = response.errors && response.errors.length > 0;
      const outcome = hasErrors ? 'error' : 'success';
      const durationMs = performance.now() - startedAt;
      clarityEvent(`${name}_${outcome}`);
      clarityTag('mutation_outcome', outcome);
      clarityTag('mutation_duration', durationBucket(durationMs));
    } catch {
      // Never corrupt a GraphQL response for telemetry
    }
    return response;
  });
});
